import React, { useEffect, useMemo, useState } from 'react';
import { Modal } from '../common/Modal';
import { Flex, Input, Text, PrimaryButton, SecondaryButton } from '../common';
import { categoryService } from '../../services/categoryService';
import { toast } from '../../utils/notify';

export const MAX_CATEGORIES_PER_POST = 5;
export const MAX_CATEGORY_LENGTH = 30;

const slugOf = (name) => name.toLowerCase().replace(/\s+/g, '-');
const clean = (raw) => raw.trim().replace(/^#+/, '').trim().replace(/\s+/g, ' ').slice(0, MAX_CATEGORY_LENGTH);

/**
 * Modal for choosing a post's categories from the creator's existing ones, or typing new ones
 * (saved as soon as you add them). Changes are applied on Done and discarded on close.
 */
const CategoryPickerModal = ({ isOpen, onClose, value, onChange }) => {
  const [selected, setSelected] = useState(value);
  const [existing, setExisting] = useState([]);
  const [draft, setDraft] = useState('');
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!isOpen) return undefined;
    setSelected(value);
    setDraft('');
    let active = true;
    categoryService.getMyCategories()
      .then((list) => active && setExisting(list || []))
      .catch(() => {});
    return () => { active = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  const selectedSlugs = useMemo(() => new Set(selected.map(slugOf)), [selected]);
  const atLimit = selected.length >= MAX_CATEGORIES_PER_POST;

  // Existing categories first, then any newly typed ones that aren't saved yet
  const options = useMemo(() => {
    const names = existing.map((c) => c.name);
    const known = new Set(names.map(slugOf));
    return [...names, ...selected.filter((n) => !known.has(slugOf(n)))];
  }, [existing, selected]);

  const toggle = (name) => {
    const slug = slugOf(name);
    if (selectedSlugs.has(slug)) {
      setSelected(selected.filter((n) => slugOf(n) !== slug));
    } else if (!atLimit) {
      setSelected([...selected, name]);
    }
  };

  // Creates the category right away so it is saved even if the post is never submitted
  const handleAdd = async (e) => {
    e.preventDefault();
    const name = clean(draft);
    if (!name || creating) return;

    const match = existing.find((c) => c.slug === slugOf(name));
    if (match) {
      if (!selectedSlugs.has(match.slug) && !atLimit) setSelected([...selected, match.name]);
      setDraft('');
      return;
    }

    setCreating(true);
    try {
      const created = await categoryService.createCategory(name);
      setExisting((prev) => [...prev, created]);
      if (!atLimit) setSelected((prev) => [...prev, created.name]);
      setDraft('');
    } catch (error) {
      toast.error(error.message);
    } finally {
      setCreating(false);
    }
  };

  const handleDone = () => {
    onChange(selected);
    onClose();
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Post categories"
      subtitle={`Pick up to ${MAX_CATEGORIES_PER_POST} to help people find this post.`}
      maxWidth="480px"
      footer={
        <Flex $align="center" $justify="space-between" $gap="12px">
          <Text $muted $small>{selected.length}/{MAX_CATEGORIES_PER_POST} selected</Text>
          <Flex $gap="8px">
            <SecondaryButton $rounded $size="small" $compact onClick={onClose}>Cancel</SecondaryButton>
            <PrimaryButton $rounded $size="small" $compact onClick={handleDone}>Done</PrimaryButton>
          </Flex>
        </Flex>
      }
    >
      <Flex as="form" onSubmit={handleAdd} $align="center" $gap="8px">
        <div style={{ flex: 1, minWidth: 0 }}>
          <Input
            rounded
            marginBottom="0"
            value={draft}
            maxLength={MAX_CATEGORY_LENGTH}
            placeholder="Create a new category"
            disabled={atLimit || creating}
            onChange={(e) => setDraft(e.target.value)}
          />
        </div>
        <SecondaryButton type="submit" $rounded $size="small" $compact $loading={creating} disabled={atLimit || !clean(draft)}>Add</SecondaryButton>
      </Flex>

      {options.length > 0 ? (
        <Flex
          $wrap="nowrap"
          $gap="8px"
          // Single sideways-scrolling row; padding leaves room for button shadows inside the scroll area
          style={{ margin: '12px 0', padding: '4px 4px 8px', overflowX: 'auto' }}
        >
          {options.map((name) => {
            const active = selectedSlugs.has(slugOf(name));
            const CategoryButton = active ? PrimaryButton : SecondaryButton;
            return (
              <CategoryButton
                key={slugOf(name)}
                type="button"
                $rounded
                $size="small"
                $compact
                disabled={!active && atLimit}
                style={{ flexShrink: 0, whiteSpace: 'nowrap' }}
                onClick={() => toggle(name)}
              >
                {name}
              </CategoryButton>
            );
          })}
        </Flex>
      ) : (
        <Text $muted $small style={{ display: 'block', margin: '16px 0' }}>
          You don't have any categories yet. Create your first one above.
        </Text>
      )}
    </Modal>
  );
};

export default CategoryPickerModal;
